import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom-v5-compat";
import { DevTool } from "@apac/devtool";
import { FeatureFlagsProvider, useBooleanFlag } from "@apac/feature-flags";
import { getMaintenanceProps, Maintenance } from "@apac/ui";
import type { CreditCardListSuccess } from "@b2bportal/card-api";
import { Funnel } from "@b2bportal/core-types";
import { LazyLoading } from "@b2bportal/core-ui";
import { hsbcAccountsApi } from "@b2bportal/hsbc-accounts-api";
import { hsbcWalletApi } from "@b2bportal/hsbc-wallet-api";
import { axiosInstance, listPaymentMethods, trackEvent } from "@hopper-b2b/api";
import { logger } from "@hopper-b2b/datadog";
import { useTrackingProperties } from "@hopper-b2b/experiments";
import { Portal } from "@hopper-b2b/portal";
import { AgentImpersonation } from "@hopper-b2b/self-serve";
import { AiAssistChatSupportProvider } from "@hopper-b2b/self-serve";
import { LAUNCHED_APPLICATION } from "@hopper-b2b/types";
import {
  getLandingScreen,
  PATH_AGENT_IMPERSONATION,
  PATH_MAINTENANCE,
  UtmTrackingProvider,
} from "@hopper-b2b/utilities";
import { AuthProvider, ProtectedRoute, useAuth } from "@hsbc/auth";
import { getInitialFlightMachineContext } from "@hsbc/flights";
import { FlightsExchangePage } from "@hsbc/flights";
import {
  AppTrackingProvider,
  AxiosInterceptorWrapper,
  ComingSoon,
  GoogleMapsApiKeyProvider,
  NewUserOnboarding,
  Timeout,
  PromotionsProvider,
  FaqPage,
  Homepage,
  HOTEL_SEARCH_MODE,
  HotelSearchDialog,
  CarSearchDialog,
  Search,
  TermsAndConditions,
  PrivacyPolicyPage,
  WalletPage,
  WatchesPage,
  DisruptionExercisePage,
  CAR_SEARCH_MODE,
  useUnavailableRedirect,
  useHtmlDirection,
} from "@hsbc/portal";
import { getFiatPointsBalance, getPointsBalance } from "@hsbc/rewards";
import {
  FeatureFlagNames,
  apiConfig,
  CARS_PATH_RELATIVE,
  COMING_SOON_PATH,
  DISRUPTION_EXERCISE_RELATIVE,
  FLIGHTS_PATH_RELATIVE,
  FREQUENT_ASK_QUESTIONS_PATH,
  HOME_PATH_RELATIVE,
  HOTELS_PATH_RELATIVE,
  INVALID_SESSION_PATH,
  MOCK_LOGIN_PATH,
  PRIVACY_POLICY_PATH,
  SEARCH_CALENDAR_PATH_RELATIVE,
  SEARCH_DESTINATION_PATH_RELATIVE,
  SEARCH_LOCATION_PATH_RELATIVE,
  SEARCH_ORIGIN_PATH_RELATIVE,
  TERMS_AND_CONDITIONS_PATH,
  USER_ONBOARD_PATH_RELATIVE,
  WALLET_PATH_RELATIVE,
  SEARCH_DROP_OFF_PATH_RELATIVE,
  SEARCH_PICKUP_PATH_RELATIVE,
  isProductionEnv,
} from "@hsbc/utilities";
import { DirectionProvider } from "@radix-ui/react-direction";
import config, { environmentConfig } from "../config";
import { branding } from "../config/branding";
import "./App.scss";
import { MockLoginPage } from "./pages/MockLoginPage";
import store from "./store";

const FlightsApp = lazy(() => import("./pages/Flights"));
const TripsApp = lazy(() => import("./pages/Trips"));
const HotelsApp = lazy(() => import("./pages/Hotels"));
const CarsApp = lazy(() => import("./pages/Cars"));

const AppContent = () => {
  const maintenanceProps = getMaintenanceProps({
    timedMaintenanceModeStartTime:
      environmentConfig.timedMaintenanceModeStartTime,
    timedMaintenanceModeEndTime: environmentConfig.timedMaintenanceModeEndTime,
    timedMaintenanceModeEnabled: environmentConfig.timedMaintenanceModeEnabled,
    preAuthMaintenanceModeEnabled: environmentConfig.preAuthMaintenanceEnabled,
    maintenanceTitle: environmentConfig.preAuthMaintenanceTitle,
    maintenanceSubtitle: environmentConfig.preAuthMaintenanceSubtitle,
  });

  const disableHotels = useBooleanFlag(FeatureFlagNames.DISABLE_HOTELS);
  const disableCars = useBooleanFlag(FeatureFlagNames.DISABLE_CARS);
  const disableFlights = useBooleanFlag(FeatureFlagNames.DISABLE_FLIGHTS);
  const [launchEventSent, setLaunchEventSent] = useState<
    "idle" | "pending" | "done"
  >("idle");

  const { isLoggedIn } = useAuth();
  const { direction } = useHtmlDirection();
  const trackingProperties = useTrackingProperties({
    landing_screen: getLandingScreen(),
    url: document.location.pathname,
  });

  useUnavailableRedirect();

  useEffect(() => {
    if (launchEventSent !== "idle" || !isLoggedIn) return;
    setLaunchEventSent("pending");
    (async () => {
      const [creditSummary, accountsRewards, paymentMethods] =
        await Promise.all([
          hsbcWalletApi(axiosInstance).apiV0WalletCreditSummaryPost(),
          hsbcAccountsApi(axiosInstance).apiV0AccountsRewardsPost(),
          listPaymentMethods({}),
        ]);
      const creditBalance = creditSummary.data?.balance;
      const pointsBalance = getPointsBalance(accountsRewards.data);
      const pointsBalanceFiat = getFiatPointsBalance(accountsRewards.data);
      const registeredCardBins =
        (paymentMethods as CreditCardListSuccess).value?.map(
          (card) => card.bin
        ) || [];
      trackEvent({
        eventName: LAUNCHED_APPLICATION,
        properties: {
          ...trackingProperties,
          is_agent_session: window.location.pathname.includes("impersonation"),
          customer_credit_balance_sgd: creditBalance?.value,
          customer_points_balance: pointsBalance?.value,
          customer_points_balance_sgd: pointsBalanceFiat?.value,
          customer_registered_card_bins: registeredCardBins,
        },
      })
        .catch((error) => {
          logger()?.error("Error tracking launch app event:", error);
        })
        .finally(() => setLaunchEventSent("done"));
    })();
  }, [isLoggedIn, launchEventSent, trackingProperties]);

  return (
    <Portal config={config} store={store}>
      <AppTrackingProvider>
        <AxiosInterceptorWrapper>
          <AiAssistChatSupportProvider>
            <DirectionProvider dir={direction}>
              <Routes>
                {/* Open routes */}
                <Route
                  key="maintenance"
                  path={`${PATH_MAINTENANCE}`}
                  element={
                    <Maintenance
                      title={maintenanceProps.title}
                      subtitle={maintenanceProps.subtitle}
                    />
                  }
                />
                <Route
                  key="timeout"
                  path={`${INVALID_SESSION_PATH}`}
                  element={<Timeout />}
                />
                <Route
                  key={"coming-soon"}
                  path={COMING_SOON_PATH}
                  element={<ComingSoon />}
                />
                <Route
                  key={"mock-login"}
                  path={`${MOCK_LOGIN_PATH}/*`}
                  element={<MockLoginPage />}
                />
                <Route
                  key={"old-mock-login"}
                  path={"/mockHsbcLogin"}
                  element={<Navigate to={MOCK_LOGIN_PATH} replace />}
                />
                <Route
                  key={PATH_AGENT_IMPERSONATION}
                  path={PATH_AGENT_IMPERSONATION}
                  element={<AgentImpersonation />}
                />
                <Route
                  key={"privacy-policy"}
                  path={PRIVACY_POLICY_PATH}
                  element={
                    <Suspense fallback={<LazyLoading />}>
                      <PrivacyPolicyPage />
                    </Suspense>
                  }
                />
                <Route
                  key={"terms"}
                  path={TERMS_AND_CONDITIONS_PATH}
                  element={<TermsAndConditions />}
                />
                <Route
                  key={"faq"}
                  path={FREQUENT_ASK_QUESTIONS_PATH}
                  element={<FaqPage />}
                />

                {/* Protected routes; logged in required */}
                {maintenanceProps.enabled ? (
                  <Route
                    key={"maintenance"}
                    path="/*"
                    element={
                      <Maintenance
                        title={maintenanceProps.title}
                        subtitle={maintenanceProps.subtitle}
                      />
                    }
                  />
                ) : (
                  <Route
                    key={"protected"}
                    path="/*"
                    element={<ProtectedRoute />}
                  >
                    <Route
                      path="*"
                      element={<Navigate to={HOME_PATH_RELATIVE} replace />}
                    />

                    <Route
                      key={"home"}
                      index
                      element={<Homepage funnel={Funnel.Lodging} />}
                    ></Route>
                    {/* Air Routes */}
                    {!disableFlights && (
                      <Route
                        path={`${FLIGHTS_PATH_RELATIVE}?`}
                        element={<Outlet />}
                      >
                        <Route
                          key={"home-flights"}
                          index
                          element={<Homepage funnel={Funnel.Air} />}
                        />
                        <Route
                          key={"flights"}
                          path={`*`}
                          element={
                            <Suspense fallback={<LazyLoading />}>
                              <FlightsApp />
                            </Suspense>
                          }
                        />
                      </Route>
                    )}

                    {/* Hotels Routes */}
                    {!disableHotels && (
                      <Route
                        key="hotels"
                        path={`${HOTELS_PATH_RELATIVE}?`}
                        element={<Outlet />}
                      >
                        <Route
                          key={"home-hotels"}
                          index
                          element={<Homepage funnel={Funnel.Lodging} />}
                        />

                        <Route
                          key="hotel-search-location"
                          path={SEARCH_LOCATION_PATH_RELATIVE}
                          element={
                            <HotelSearchDialog
                              mode={HOTEL_SEARCH_MODE.LOCATION}
                            />
                          }
                        />

                        <Route
                          key="hotel-search-calendar"
                          path={SEARCH_CALENDAR_PATH_RELATIVE}
                          element={
                            <HotelSearchDialog
                              mode={HOTEL_SEARCH_MODE.CALENDAR}
                            />
                          }
                        />

                        <Route
                          key={"hotels-app"}
                          path={`*`}
                          element={
                            <Suspense fallback={<LazyLoading />}>
                              <HotelsApp />
                            </Suspense>
                          }
                        />
                      </Route>
                    )}

                    {/* Cars Routes */}
                    {!disableCars && (
                      <Route
                        key="cars"
                        path={`${CARS_PATH_RELATIVE}?`}
                        element={<Outlet />}
                      >
                        <Route
                          key={"home-cars"}
                          index
                          element={<Homepage funnel={Funnel.Ground} />}
                        />
                        <Route
                          key="car-search-pickup"
                          path={SEARCH_PICKUP_PATH_RELATIVE}
                          element={
                            <CarSearchDialog mode={CAR_SEARCH_MODE.PICK_UP} />
                          }
                        />
                        <Route
                          key="car-search-dropoff"
                          path={SEARCH_DROP_OFF_PATH_RELATIVE}
                          element={
                            <CarSearchDialog mode={CAR_SEARCH_MODE.DROP_OFF} />
                          }
                        />
                        <Route
                          key={"cars-app"}
                          path={`*`}
                          element={
                            <Suspense fallback={<LazyLoading />}>
                              <CarsApp />
                            </Suspense>
                          }
                        />
                      </Route>
                    )}

                    <Route
                      key={"air-price-watch"}
                      path={`watches/*`}
                      element={
                        <Suspense fallback={<LazyLoading />}>
                          <WatchesPage />
                        </Suspense>
                      }
                    />
                    <Route key="trips" path="trips" element={<Outlet />}>
                      <Route
                        key={"flights-exchange"}
                        path={`flights/:itineraryId/exchange/*`}
                        element={
                          <Suspense fallback={<LazyLoading />}>
                            <FlightsExchangePage />
                          </Suspense>
                        }
                      />
                      <Route
                        key="itinerary"
                        path={"*"}
                        element={
                          <Suspense fallback={<LazyLoading />}>
                            <TripsApp />
                          </Suspense>
                        }
                      />
                    </Route>
                    <Route
                      key="search-2"
                      path={SEARCH_DESTINATION_PATH_RELATIVE}
                      element={<Search />}
                    />

                    <Route
                      key="search-1"
                      path={SEARCH_ORIGIN_PATH_RELATIVE}
                      element={<Search isOrigin />}
                    />

                    <Route
                      key="onboarding"
                      path={USER_ONBOARD_PATH_RELATIVE}
                      element={<NewUserOnboarding />}
                    />

                    <Route
                      key={"wallet"}
                      path={`${WALLET_PATH_RELATIVE}/*`}
                      element={
                        <Suspense fallback={<LazyLoading />}>
                          <WalletPage />
                        </Suspense>
                      }
                    />
                    <Route
                      key={"disruption-exercise"}
                      path={`${DISRUPTION_EXERCISE_RELATIVE}/*`}
                      element={
                        <Suspense fallback={<LazyLoading />}>
                          <DisruptionExercisePage />
                        </Suspense>
                      }
                    />
                  </Route>
                )}
              </Routes>
            </DirectionProvider>
          </AiAssistChatSupportProvider>
        </AxiosInterceptorWrapper>
      </AppTrackingProvider>
    </Portal>
  );
};

const AppInProvider = () => {
  const authState = useAuth();

  return (
    // Note that the <Portal /> component is already wrapped in a feature flags provider. However, this provider is mostly a empty skeleton lacks types, API requests, and value hooks. HTS Foundation is not ready to add logic to it yet. We can migrate once it's fully implemented.
    // There is also an old FeatureFlagContextProps, which contains only boolean type flags not supposed to be used as experiment context anymore
    <UtmTrackingProvider>
      <FeatureFlagsProvider
        apiConfig={apiConfig}
        initState={{
          experiments: getInitialFlightMachineContext().featureFlags as any,
          featureFlags: [],
        }}
        isLoggedIn={authState.isLoggedIn}
      >
        <DevTool
          isProduction={isProductionEnv()}
          appInfo={{
            appName: "hsbc-qa",
            env: environmentConfig.env,
            version: environmentConfig.version,
          }}
          authInfo={{
            isLoggedIn: authState.isLoggedIn,
            userName: authState.userInfo?.firstName
              ? `${authState.userInfo.firstName} ${authState.userInfo.lastName ?? ""}`.trim()
              : undefined,
            email: authState.userInfo?.email,
            userId: authState.userInfo?.hopperUserId,
          }}
          locale={{
            current: "",
            supported: branding.supportedLanguages,
          }}
          showLanguagePicker
          mockLoginUrl={MOCK_LOGIN_PATH}
        >
          <GoogleMapsApiKeyProvider>
            <PromotionsProvider>
              <AppContent />
            </PromotionsProvider>
          </GoogleMapsApiKeyProvider>
        </DevTool>
      </FeatureFlagsProvider>
    </UtmTrackingProvider>
  );
};

export const App = () => {
  return (
    <AuthProvider>
      <AppInProvider />
    </AuthProvider>
  );
};
